/**
 * KPI Service - Fleet Premium
 *
 * Servico para calculo de KPIs de roteirizacao:
 * - Taxa de entregas no prazo
 * - Eficiencia de rotas
 * - Custos operacionais
 * - Performance de motoristas
 * - Metricas de frota
 */

import { calculatePlanCost, loadCostConfig } from './costCalculationService';

// =======================================
// Constantes
// =======================================

/**
 * Periodos para analise
 */
export const KPI_PERIODS = {
  TODAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
  CUSTOM: 'custom',
};

/**
 * Status de entrega
 */
export const DELIVERY_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  SKIPPED: 'skipped',
  LATE: 'late',
};

/**
 * Categorias de KPI
 */
export const KPI_CATEGORIES = {
  DELIVERIES: 'deliveries',
  EFFICIENCY: 'efficiency',
  COSTS: 'costs',
  FLEET: 'fleet',
  DRIVERS: 'drivers',
};

// =======================================
// Funcoes de Calculo de KPIs
// =======================================

/**
 * Calcula KPIs de entregas
 * @param {array} plans - Lista de planos de rota
 * @returns {object} KPIs de entregas
 */
export const calculateDeliveryKPIs = (plans = []) => {
  const allStops = plans.flatMap((p) => p.stops || []);

  const total = allStops.length;
  const completed = allStops.filter((s) => s.status === DELIVERY_STATUS.COMPLETED).length;
  const failed = allStops.filter((s) => s.status === DELIVERY_STATUS.FAILED).length;
  const skipped = allStops.filter((s) => s.status === DELIVERY_STATUS.SKIPPED).length;
  const pending = allStops.filter(
    (s) => s.status === DELIVERY_STATUS.PENDING || s.status === DELIVERY_STATUS.IN_PROGRESS
  ).length;

  // Entregas no prazo (chegada antes ou igual ao estimado + 15min tolerancia)
  const onTimeDeliveries = allStops.filter((s) => {
    if (s.status !== DELIVERY_STATUS.COMPLETED) return false;
    if (!s.actualArrival || !s.estimatedArrival) return true; // Assume on-time se nao tiver dados
    const tolerance = 15 * 60 * 1000; // 15 minutos em ms
    return new Date(s.actualArrival) <= new Date(s.estimatedArrival).getTime() + tolerance;
  }).length;

  const completionRate = total > 0 ? (completed / total) * 100 : 0;
  const onTimeRate = completed > 0 ? (onTimeDeliveries / completed) * 100 : 0;
  const failureRate = total > 0 ? (failed / total) * 100 : 0;

  return {
    total,
    completed,
    failed,
    skipped,
    pending,
    onTimeDeliveries,
    completionRate: Math.round(completionRate * 10) / 10,
    onTimeRate: Math.round(onTimeRate * 10) / 10,
    failureRate: Math.round(failureRate * 10) / 10,
    averagePerPlan: plans.length > 0 ? Math.round(total / plans.length) : 0,
  };
};

/**
 * Calcula KPIs de eficiencia
 * @param {array} plans - Lista de planos de rota
 * @returns {object} KPIs de eficiencia
 */
export const calculateEfficiencyKPIs = (plans = []) => {
  if (plans.length === 0) {
    return {
      totalDistance: 0,
      totalDuration: 0,
      averageDistancePerPlan: 0,
      averageDurationPerPlan: 0,
      averageDistancePerDelivery: 0,
      averageDurationPerDelivery: 0,
      routeUtilization: 0,
      optimizationSavings: 0,
      optimizationSavingsPercent: 0,
    };
  }

  const allVehicles = plans.flatMap((p) => p.vehicles || []);
  const allStops = plans.flatMap((p) => p.stops || []);

  // Distancias e duracoes - combinar dados de veiculos e planos
  let totalDistance = allVehicles.reduce((sum, v) => sum + (v.estimatedDistance || 0), 0);
  let totalDuration = allVehicles.reduce((sum, v) => sum + (v.estimatedDuration || 0), 0);

  // Se veiculos nao tiverem dados, usar dados do plano
  if (totalDistance === 0) {
    totalDistance = plans.reduce((sum, p) => sum + (p.totalDistance || 0), 0);
  }
  if (totalDuration === 0) {
    totalDuration = plans.reduce((sum, p) => sum + (p.totalDuration || 0), 0);
  }

  const actualDistance = allVehicles.reduce((sum, v) => sum + (v.actualDistance || v.estimatedDistance || 0), 0) || totalDistance;
  const actualDuration = allVehicles.reduce((sum, v) => sum + (v.actualDuration || v.estimatedDuration || 0), 0) || totalDuration;

  const deliveryCount = allStops.filter((s) => s.status === DELIVERY_STATUS.COMPLETED).length || allStops.length || 1;

  // Calcular utilizacao de rota (actual vs planned)
  const routeUtilization = totalDistance > 0 ? (actualDistance / totalDistance) * 100 : 100;

  // Economia estimada (comparando rota otimizada vs rota naive)
  // Assumimos que rota naive seria 30% mais longa em media
  const naiveDistanceEstimate = totalDistance * 1.3;
  const optimizationSavings = naiveDistanceEstimate - totalDistance;

  // Evitar NaN: se nao houver distancia, economia e 0
  const optimizationSavingsPercent = totalDistance > 0 && naiveDistanceEstimate > 0
    ? Math.round((1 - totalDistance / naiveDistanceEstimate) * 100)
    : 0;

  return {
    totalDistance: Math.round(totalDistance / 1000), // km
    totalDuration: Math.round(totalDuration / 60), // minutos
    actualDistance: Math.round(actualDistance / 1000),
    actualDuration: Math.round(actualDuration / 60),
    averageDistancePerPlan: plans.length > 0 ? Math.round(totalDistance / plans.length / 1000) : 0,
    averageDurationPerPlan: plans.length > 0 ? Math.round(totalDuration / plans.length / 60) : 0,
    averageDistancePerDelivery: deliveryCount > 0 ? Math.round(totalDistance / deliveryCount / 1000 * 10) / 10 : 0,
    averageDurationPerDelivery: deliveryCount > 0 ? Math.round(totalDuration / deliveryCount / 60) : 0,
    routeUtilization: Math.round(routeUtilization * 10) / 10,
    optimizationSavings: Math.round(optimizationSavings / 1000), // km economizados
    optimizationSavingsPercent,
  };
};

/**
 * Calcula KPIs de custos
 * @param {array} plans - Lista de planos de rota
 * @param {object} costConfig - Configuracao de custos
 * @returns {object} KPIs de custos
 */
export const calculateCostKPIs = (plans = [], costConfig = null) => {
  const config = costConfig || loadCostConfig();

  if (plans.length === 0) {
    return {
      totalCost: 0,
      fuelCost: 0,
      driverCost: 0,
      maintenanceCost: 0,
      tollsCost: 0,
      otherCosts: 0,
      costPerKm: 0,
      costPerDelivery: 0,
      averageCostPerPlan: 0,
      costBreakdown: [],
    };
  }

  // Calcular custos de todos os planos
  let totalCost = 0;
  let fuelCost = 0;
  let driverCost = 0;
  let maintenanceCost = 0;
  let tollsCost = 0;
  let otherCosts = 0;
  let totalDistanceMeters = 0;

  plans.forEach((plan) => {
    // Obter distancia e duracao: priorizar dados do plano, depois somar dos veiculos
    const vehiclesDistance = (plan.vehicles || []).reduce((sum, v) => sum + (v.estimatedDistance || 0), 0);
    const vehiclesDuration = (plan.vehicles || []).reduce((sum, v) => sum + (v.estimatedDuration || 0), 0);

    // Usar dados do plano se veiculos nao tiverem (plan.totalDistance em metros, totalDuration em segundos)
    const planDistance = plan.totalDistance || vehiclesDistance || 0;
    const planDuration = plan.totalDuration || vehiclesDuration || 0;

    totalDistanceMeters += planDistance;

    // Se tiver veiculos com dados, calcular por veiculo
    if (vehiclesDistance > 0) {
      const routes = (plan.vehicles || []).map((v) => ({
        distance: (v.estimatedDistance || 0),
        duration: (v.estimatedDuration || 0),
        steps: (plan.stops || []).filter((s) => s.vehicleId === v.id),
      }));

      const planCostCalc = calculatePlanCost(routes, config);
      totalCost += planCostCalc.totals.totalCost;
      fuelCost += planCostCalc.totals.fuelCost;
      driverCost += planCostCalc.totals.driverCost;
      maintenanceCost += planCostCalc.totals.maintenanceCost;
      tollsCost += planCostCalc.totals.tollsCost;
      otherCosts += planCostCalc.totals.otherCosts;
    } else if (planDistance > 0) {
      // Calcular custos baseado nos dados do plano diretamente
      const routes = [{
        distance: planDistance,
        duration: planDuration,
        steps: plan.stops || [],
      }];

      const planCostCalc = calculatePlanCost(routes, config);
      totalCost += planCostCalc.totals.totalCost;
      fuelCost += planCostCalc.totals.fuelCost;
      driverCost += planCostCalc.totals.driverCost;
      maintenanceCost += planCostCalc.totals.maintenanceCost;
      tollsCost += planCostCalc.totals.tollsCost;
      otherCosts += planCostCalc.totals.otherCosts;
    } else if (plan.totalCost && plan.totalCost > 0) {
      // Usar custo total ja calculado no plano (fallback)
      // Distribuir proporcionalmente baseado em valores medios
      const planTotalCost = plan.totalCost;
      totalCost += planTotalCost;
      // Distribuicao estimada: 40% combustivel, 35% motorista, 10% manutencao, 10% pedagios, 5% outros
      fuelCost += planTotalCost * 0.40;
      driverCost += planTotalCost * 0.35;
      maintenanceCost += planTotalCost * 0.10;
      tollsCost += planTotalCost * 0.10;
      otherCosts += planTotalCost * 0.05;
    }
  });

  const allStops = plans.flatMap((p) => p.stops || []);
  const deliveryCount = allStops.filter((s) => s.status === DELIVERY_STATUS.COMPLETED).length || allStops.length;
  const totalDistanceKm = totalDistanceMeters / 1000;

  return {
    totalCost: Math.round(totalCost * 100) / 100,
    fuelCost: Math.round(fuelCost * 100) / 100,
    driverCost: Math.round(driverCost * 100) / 100,
    maintenanceCost: Math.round(maintenanceCost * 100) / 100,
    tollsCost: Math.round(tollsCost * 100) / 100,
    otherCosts: Math.round(otherCosts * 100) / 100,
    costPerKm: totalDistanceKm > 0 ? Math.round((totalCost / totalDistanceKm) * 100) / 100 : 0,
    costPerDelivery: deliveryCount > 0 ? Math.round((totalCost / deliveryCount) * 100) / 100 : 0,
    averageCostPerPlan: plans.length > 0 ? Math.round((totalCost / plans.length) * 100) / 100 : 0,
    costBreakdown: [
      { name: 'Combustivel', value: fuelCost, color: '#f59e0b' },
      { name: 'Motorista', value: driverCost, color: '#00d4aa' },
      { name: 'Manutencao', value: maintenanceCost, color: '#6b7280' },
      { name: 'Pedagios', value: tollsCost, color: '#3b82f6' },
      { name: 'Outros', value: otherCosts, color: '#8b5cf6' },
    ],
  };
};

/**
 * Calcula KPIs de frota
 * @param {array} plans - Lista de planos de rota
 * @param {array} vehicles - Lista de veiculos
 * @returns {object} KPIs de frota
 */
export const calculateFleetKPIs = (plans = [], vehicles = []) => {
  const allVehicles = plans.flatMap((p) => p.vehicles || []);
  const uniqueVehicleIds = new Set(allVehicles.map((v) => v.deviceId));

  const vehiclesUsed = uniqueVehicleIds.size;
  const totalVehicles = vehicles.length;
  const fleetUtilization = totalVehicles > 0 ? (vehiclesUsed / totalVehicles) * 100 : 0;

  // Calcular metricas por veiculo
  const vehicleStats = {};
  allVehicles.forEach((v) => {
    if (!vehicleStats[v.deviceId]) {
      vehicleStats[v.deviceId] = {
        id: v.deviceId,
        trips: 0,
        distance: 0,
        duration: 0,
        deliveries: 0,
      };
    }
    vehicleStats[v.deviceId].trips += 1;
    vehicleStats[v.deviceId].distance += v.estimatedDistance || 0;
    vehicleStats[v.deviceId].duration += v.estimatedDuration || 0;
  });

  // Adicionar contagem de entregas
  plans.forEach((plan) => {
    (plan.stops || []).forEach((stop) => {
      if (stop.vehicleId && vehicleStats[stop.vehicleId]) {
        vehicleStats[stop.vehicleId].deliveries += 1;
      }
    });
  });

  const vehicleStatsList = Object.values(vehicleStats).map((v) => ({
    ...v,
    distanceKm: Math.round(v.distance / 1000),
    durationHours: Math.round(v.duration / 3600 * 10) / 10,
    averageDeliveriesPerTrip: v.trips > 0 ? Math.round(v.deliveries / v.trips * 10) / 10 : 0,
  }));

  // Top veiculos por entregas
  const topVehicles = vehicleStatsList
    .sort((a, b) => b.deliveries - a.deliveries)
    .slice(0, 5);

  return {
    totalVehicles,
    vehiclesUsed,
    fleetUtilization: Math.round(fleetUtilization * 10) / 10,
    averageTripsPerVehicle: vehiclesUsed > 0 ? Math.round(allVehicles.length / vehiclesUsed * 10) / 10 : 0,
    vehicleStats: vehicleStatsList,
    topVehicles,
  };
};

/**
 * Calcula KPIs de motoristas
 * @param {array} plans - Lista de planos de rota
 * @returns {object} KPIs de motoristas
 */
export const calculateDriverKPIs = (plans = []) => {
  const allVehicles = plans.flatMap((p) => p.vehicles || []);
  const allStops = plans.flatMap((p) => p.stops || []);

  // Agrupar por motorista (usando driverId do veiculo)
  const driverStats = {};

  allVehicles.forEach((v) => {
    const driverId = v.driverId || `vehicle_${v.deviceId}`;
    if (!driverStats[driverId]) {
      driverStats[driverId] = {
        id: driverId,
        trips: 0,
        distance: 0,
        duration: 0,
        deliveries: 0,
        completed: 0,
        onTime: 0,
      };
    }
    driverStats[driverId].trips += 1;
    driverStats[driverId].distance += v.estimatedDistance || 0;
    driverStats[driverId].duration += v.estimatedDuration || 0;
  });

  // Adicionar metricas de entregas
  allStops.forEach((stop) => {
    const vehicle = allVehicles.find((v) => v.id === stop.vehicleId);
    if (vehicle) {
      const driverId = vehicle.driverId || `vehicle_${vehicle.deviceId}`;
      if (driverStats[driverId]) {
        driverStats[driverId].deliveries += 1;
        if (stop.status === DELIVERY_STATUS.COMPLETED) {
          driverStats[driverId].completed += 1;
          // Verificar se foi no prazo
          if (stop.actualArrival && stop.estimatedArrival) {
            const tolerance = 15 * 60 * 1000;
            if (new Date(stop.actualArrival) <= new Date(stop.estimatedArrival).getTime() + tolerance) {
              driverStats[driverId].onTime += 1;
            }
          } else {
            driverStats[driverId].onTime += 1; // Assume on-time sem dados
          }
        }
      }
    }
  });

  const driverStatsList = Object.values(driverStats).map((d) => ({
    ...d,
    distanceKm: Math.round(d.distance / 1000),
    durationHours: Math.round(d.duration / 3600 * 10) / 10,
    completionRate: d.deliveries > 0 ? Math.round((d.completed / d.deliveries) * 100) : 0,
    onTimeRate: d.completed > 0 ? Math.round((d.onTime / d.completed) * 100) : 0,
    efficiency: d.duration > 0 ? Math.round((d.completed / (d.duration / 3600)) * 10) / 10 : 0, // entregas/hora
  }));

  // Top motoristas por eficiencia
  const topDrivers = driverStatsList
    .sort((a, b) => b.efficiency - a.efficiency)
    .slice(0, 5);

  return {
    totalDrivers: Object.keys(driverStats).length,
    driverStats: driverStatsList,
    topDrivers,
    averageEfficiency: driverStatsList.length > 0
      ? Math.round(driverStatsList.reduce((sum, d) => sum + d.efficiency, 0) / driverStatsList.length * 10) / 10
      : 0,
    averageCompletionRate: driverStatsList.length > 0
      ? Math.round(driverStatsList.reduce((sum, d) => sum + d.completionRate, 0) / driverStatsList.length)
      : 0,
  };
};

/**
 * Calcula todos os KPIs
 * @param {array} plans - Lista de planos de rota
 * @param {array} vehicles - Lista de veiculos
 * @param {object} costConfig - Configuracao de custos
 * @returns {object} Todos os KPIs
 */
export const calculateAllKPIs = (plans = [], vehicles = [], costConfig = null) => {
  return {
    deliveries: calculateDeliveryKPIs(plans),
    efficiency: calculateEfficiencyKPIs(plans),
    costs: calculateCostKPIs(plans, costConfig),
    fleet: calculateFleetKPIs(plans, vehicles),
    drivers: calculateDriverKPIs(plans),
    meta: {
      plansCount: plans.length,
      calculatedAt: new Date().toISOString(),
    },
  };
};

// =======================================
// Funcoes de Tendencia/Comparacao
// =======================================

/**
 * Calcula tendencia entre dois periodos
 * @param {number} current - Valor atual
 * @param {number} previous - Valor anterior
 * @returns {object} Dados de tendencia
 */
export const calculateTrend = (current, previous) => {
  if (previous === 0) {
    return {
      current,
      previous,
      change: current,
      changePercent: current > 0 ? 100 : 0,
      direction: current > 0 ? 'up' : 'neutral',
    };
  }

  const change = current - previous;
  const changePercent = (change / previous) * 100;

  return {
    current,
    previous,
    change: Math.round(change * 100) / 100,
    changePercent: Math.round(changePercent * 10) / 10,
    direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
  };
};

/**
 * Gera dados para grafico de serie temporal
 * @param {array} plans - Lista de planos com datas
 * @param {string} metric - Metrica a extrair
 * @param {string} period - Periodo de agregacao
 * @returns {array} Dados para grafico
 */
export const generateTimeSeriesData = (plans = [], metric = 'deliveries', period = 'day') => {
  if (plans.length === 0) return [];

  // Agrupar por periodo
  const grouped = {};

  plans.forEach((plan) => {
    const date = new Date(plan.date || plan.createdAt);
    let key;

    switch (period) {
      case 'day':
        key = date.toISOString().split('T')[0];
        break;
      case 'week': {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
        break;
      }
      case 'month':
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
      default:
        key = date.toISOString().split('T')[0];
    }

    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(plan);
  });

  // Calcular metrica para cada periodo
  return Object.entries(grouped)
    .map(([date, periodPlans]) => {
      let value;
      switch (metric) {
        case 'deliveries':
          value = periodPlans.reduce((sum, p) => sum + (p.stops?.length || 0), 0);
          break;
        case 'distance':
          value = periodPlans.reduce(
            (sum, p) => sum + (p.vehicles || []).reduce((vs, v) => vs + (v.estimatedDistance || 0), 0),
            0
          ) / 1000;
          break;
        case 'cost': {
          const costKPIs = calculateCostKPIs(periodPlans);
          value = costKPIs.totalCost;
          break;
        }
        case 'completion': {
          const deliveryKPIs = calculateDeliveryKPIs(periodPlans);
          value = deliveryKPIs.completionRate;
          break;
        }
        default:
          value = periodPlans.length;
      }
      return { date, value: Math.round(value * 100) / 100 };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

// =======================================
// Empty State Data
// =======================================

/**
 * Retorna KPIs vazios para quando nao ha dados
 * @returns {object} KPIs zerados
 */
export const getEmptyKPIs = () => ({
  deliveries: {
    total: 0,
    completed: 0,
    failed: 0,
    skipped: 0,
    pending: 0,
    onTimeDeliveries: 0,
    completionRate: 0,
    onTimeRate: 0,
    failureRate: 0,
    averagePerPlan: 0,
  },
  efficiency: {
    totalDistance: 0,
    totalDuration: 0,
    actualDistance: 0,
    actualDuration: 0,
    averageDistancePerPlan: 0,
    averageDurationPerPlan: 0,
    averageDistancePerDelivery: 0,
    averageDurationPerDelivery: 0,
    routeUtilization: 0,
    optimizationSavings: 0,
    optimizationSavingsPercent: 0,
  },
  costs: {
    totalCost: 0,
    fuelCost: 0,
    driverCost: 0,
    maintenanceCost: 0,
    tollsCost: 0,
    otherCosts: 0,
    costPerKm: 0,
    costPerDelivery: 0,
    averageCostPerPlan: 0,
    costBreakdown: [],
  },
  fleet: {
    totalVehicles: 0,
    vehiclesUsed: 0,
    fleetUtilization: 0,
    averageTripsPerVehicle: 0,
    topVehicles: [],
  },
  drivers: {
    totalDrivers: 0,
    averageEfficiency: 0,
    averageCompletionRate: 0,
    topDrivers: [],
  },
  trends: {
    deliveries: [],
    costs: [],
  },
  meta: {
    plansCount: 0,
    period: 'week',
    calculatedAt: new Date().toISOString(),
    isEmpty: true,
  },
});

// =======================================
// Mock Data para Demonstracao
// =======================================

/**
 * Gera dados mock para demonstracao do dashboard
 * @returns {object} Dados mock de KPIs
 */
export const generateMockKPIs = () => {
  return {
    deliveries: {
      total: 1247,
      completed: 1156,
      failed: 43,
      skipped: 28,
      pending: 20,
      onTimeDeliveries: 1089,
      completionRate: 92.7,
      onTimeRate: 94.2,
      failureRate: 3.4,
      averagePerPlan: 31,
    },
    efficiency: {
      totalDistance: 8542,
      totalDuration: 12840,
      actualDistance: 8234,
      actualDuration: 12456,
      averageDistancePerPlan: 213,
      averageDurationPerPlan: 321,
      averageDistancePerDelivery: 6.8,
      averageDurationPerDelivery: 10,
      routeUtilization: 96.4,
      optimizationSavings: 2563,
      optimizationSavingsPercent: 23,
    },
    costs: {
      totalCost: 45678.90,
      fuelCost: 18450.00,
      driverCost: 19200.00,
      maintenanceCost: 4280.50,
      tollsCost: 2148.40,
      otherCosts: 1600.00,
      costPerKm: 5.35,
      costPerDelivery: 39.52,
      averageCostPerPlan: 1142.00,
      costBreakdown: [
        { name: 'Combustivel', value: 18450, color: '#f59e0b' },
        { name: 'Motorista', value: 19200, color: '#00d4aa' },
        { name: 'Manutencao', value: 4280.5, color: '#6b7280' },
        { name: 'Pedagios', value: 2148.4, color: '#3b82f6' },
        { name: 'Outros', value: 1600, color: '#8b5cf6' },
      ],
    },
    fleet: {
      totalVehicles: 25,
      vehiclesUsed: 18,
      fleetUtilization: 72.0,
      averageTripsPerVehicle: 2.2,
      topVehicles: [
        { id: 1, name: 'Fiat Uno ABC-1234', deliveries: 156, distanceKm: 1245 },
        { id: 2, name: 'VW Gol DEF-5678', deliveries: 142, distanceKm: 1180 },
        { id: 3, name: 'Fiat Fiorino GHI-9012', deliveries: 138, distanceKm: 1320 },
        { id: 4, name: 'Renault Kangoo JKL-3456', deliveries: 125, distanceKm: 1090 },
        { id: 5, name: 'VW Saveiro MNO-7890', deliveries: 118, distanceKm: 980 },
      ],
    },
    drivers: {
      totalDrivers: 12,
      averageEfficiency: 4.8,
      averageCompletionRate: 94,
      topDrivers: [
        { id: 1, name: 'Joao Silva', efficiency: 6.2, completionRate: 98, deliveries: 186 },
        { id: 2, name: 'Maria Santos', efficiency: 5.8, completionRate: 96, deliveries: 172 },
        { id: 3, name: 'Pedro Oliveira', efficiency: 5.4, completionRate: 95, deliveries: 165 },
        { id: 4, name: 'Ana Costa', efficiency: 5.1, completionRate: 94, deliveries: 158 },
        { id: 5, name: 'Carlos Souza', efficiency: 4.9, completionRate: 93, deliveries: 152 },
      ],
    },
    trends: {
      deliveries: [
        { date: '2026-01-05', value: 145 },
        { date: '2026-01-06', value: 168 },
        { date: '2026-01-07', value: 152 },
        { date: '2026-01-08', value: 189 },
        { date: '2026-01-09', value: 176 },
        { date: '2026-01-10', value: 198 },
        { date: '2026-01-11', value: 219 },
      ],
      costs: [
        { date: '2026-01-05', value: 5420 },
        { date: '2026-01-06', value: 6180 },
        { date: '2026-01-07', value: 5890 },
        { date: '2026-01-08', value: 7230 },
        { date: '2026-01-09', value: 6540 },
        { date: '2026-01-10', value: 7120 },
        { date: '2026-01-11', value: 7298 },
      ],
    },
    meta: {
      plansCount: 40,
      period: 'week',
      calculatedAt: new Date().toISOString(),
    },
  };
};

export default {
  // Constantes
  KPI_PERIODS,
  DELIVERY_STATUS,
  KPI_CATEGORIES,
  // Calculos
  calculateDeliveryKPIs,
  calculateEfficiencyKPIs,
  calculateCostKPIs,
  calculateFleetKPIs,
  calculateDriverKPIs,
  calculateAllKPIs,
  // Tendencias
  calculateTrend,
  generateTimeSeriesData,
  // Empty/Mock
  getEmptyKPIs,
  generateMockKPIs,
};
